import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { erpPrograms, trainingTracks } from '@/lib/api';

export default function ERPProgramsPage() {
  return (
    <div className="min-h-screen pt-24" data-testid="erp-programs-page">
      {/* Hero Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-brand-50 via-white to-white">
        <div className="container-main">
          <div className="max-w-3xl">
            <div
              
              
            >
              <span className="inline-block px-4 py-2 bg-brand-100 text-brand-700 rounded-full text-sm font-medium mb-6">
                ERP Training Programs
              </span>
              <h1 className="text-4xl sm:text-5xl font-bold text-brand-900 mb-6">
                Master Industry-Leading ERP Systems
              </h1>
              <p className="text-lg text-slate-600 mb-8">
                Choose from comprehensive training programs covering the world's most sought-after 
                ERP platforms. Each program includes hands-on labs, certification preparation, and job-ready skills.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Training Tracks Overview */}
      <section className="py-12 bg-white border-y border-slate-100">
        <div className="container-main">
          <p className="text-center text-slate-500 mb-8 font-medium">All Programs Available in Three Tracks</p>
          <div className="flex flex-wrap justify-center gap-8">
            {Object.entries(trainingTracks).map(([key, track]) => (
              <div key={key} className="flex items-center space-x-3">
                <span className="text-2xl">{track.icon}</span>
                <div>
                  <p className="font-medium text-brand-900">{track.name}</p>
                  <p className="text-xs text-slate-500">{track.subtitle}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ERP Programs Bento Grid */}
      <section className="py-20 lg:py-28">
        <div className="container-main">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Featured Program - IFS */}
            <div
              
              
              
              className="lg:col-span-2 lg:row-span-2"
            >
              <Link to="/programs/ifs">
                <Card className="program-card h-full relative overflow-hidden group" data-testid="featured-program-ifs">
                  <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-blue-500 to-blue-700" />
                  <CardContent className="p-8 lg:p-10">
                    <div className="flex items-start justify-between mb-6">
                      <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${erpPrograms.ifs.color} flex items-center justify-center text-3xl`}>
                        {erpPrograms.ifs.icon}
                      </div>
                      <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">Popular</span>
                    </div>
                    <h2 className="font-heading font-bold text-2xl lg:text-3xl text-brand-900 mb-3 group-hover:text-brand-700 transition-colors">
                      {erpPrograms.ifs.fullName}
                    </h2>
                    <p className="text-brand-600 font-medium mb-4">{erpPrograms.ifs.tagline}</p>
                    <p className="text-slate-600 mb-6">{erpPrograms.ifs.description}</p>
                    
                    <div className="mb-6">
                      <p className="text-sm font-medium text-slate-500 mb-3">Key Modules</p>
                      <div className="flex flex-wrap gap-2">
                        {erpPrograms.ifs.modules.slice(0, 5).map((module, i) => (
                          <span key={i} className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-xs">
                            {module}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div className="flex items-center text-brand-600 font-medium">
                      <span>View Program Details</span>
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </div>

            {/* Other Programs */}
            {Object.entries(erpPrograms).filter(([key]) => key !== 'ifs').map(([key, program], index) => (
              <div
                key={key}
                
                
                
                
              >
                <Link to={`/programs/${key}`}>
                  <Card className="program-card h-full group" data-testid={`program-${key}`}>
                    <CardContent className="p-6">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${program.color} flex items-center justify-center text-2xl mb-4`}>
                        {program.icon}
                      </div>
                      <h3 className="font-heading font-bold text-xl text-brand-900 mb-2 group-hover:text-brand-700 transition-colors">
                        {program.fullName}
                      </h3>
                      <p className="text-slate-600 text-sm mb-4">{program.tagline}</p>
                      <div className="flex flex-wrap gap-1 mb-4">
                        {program.industries.slice(0, 3).map((ind, i) => (
                          <span key={i} className="text-xs text-slate-500">
                            {ind}{i < 2 ? ' • ' : ''}
                          </span>
                        ))}
                      </div>
                      <div className="flex items-center text-brand-600 text-sm font-medium">
                        <span>Learn more</span>
                        <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* All Programs Include */}
      <section className="py-20 lg:py-28 bg-brand-50/50">
        <div className="container-main">
          <div className="text-center mb-16">
            <h2 className="section-heading mb-4">What's Included in Every Program</h2>
            <p className="section-subheading mx-auto">
              Comprehensive training resources designed for your success
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: 'Live Sessions', desc: 'Interactive instructor-led classes with real-time Q&A' },
              { title: 'Hands-On Labs', desc: 'Practice on actual ERP systems with guided exercises' },
              { title: 'Study Materials', desc: 'Comprehensive guides, slides, and reference documents' },
              { title: 'Certification Prep', desc: 'Mock exams and exam strategy coaching' },
              { title: 'Project Work', desc: 'Real-world scenarios and implementation projects' },
              { title: 'Recording Access', desc: 'Session recordings for revision and catch-up' },
              { title: 'Community Access', desc: 'Connect with peers and alumni network' },
              { title: 'Career Support', desc: 'Resume review and interview preparation' },
            ].map((item, index) => (
              <div
                key={item.title}
                
                
                
                
                className="flex items-start space-x-3"
              >
                <CheckCircle className="w-5 h-5 text-brand-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-brand-900">{item.title}</p>
                  <p className="text-sm text-slate-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="section-heading mb-6">Not Sure Which Program is Right for You?</h2>
            <p className="text-lg text-slate-600 mb-8">
              Our career advisors can help you choose the best ERP and training track 
              based on your background, goals, and industry preferences.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button className="btn-primary" data-testid="schedule-consultation-btn">
                  Schedule Free Consultation
                </Button>
              </Link>
              <Link to="/webinar">
                <Button variant="outline" className="btn-secondary">
                  Attend Free Webinar
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
